import { useEffect, useRef, useState } from "react";
import { Room, Player, Round, Submission } from "@/hooks/useRoom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Check, Clock } from "lucide-react";
import { cluster, alignmentScore } from "@/lib/clustering";
import RoomHeader from "@/components/RoomHeader";

interface Props {
  room: Room;
  players: Player[];
  round: Round;
  submissions: Submission[];
  me: Player;
  amHost: boolean;
}

export default function GameRound({ room, players, round, submissions, me, amHost }: Props) {
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(Date.now());
  const revealedRef = useRef<string | null>(null);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const mySub = submissions.find((s) => s.player_id === me.id);
  const endsAt = round.ends_at ? new Date(round.ends_at).getTime() : 0;
  const remaining = Math.max(0, Math.ceil((endsAt - now) / 1000));
  const total = room.settings.timer ?? 30;
  const allSubmitted = players.length > 0 && submissions.length >= players.length;

  // Auto-reveal: host triggers when timer expires or everyone submitted.
  // Strict guards prevent re-firing on stale rounds after status flips.
  useEffect(() => {
    if (!amHost) return;
    if (room.status !== "playing") return;
    if (round.status !== "collecting") return;
    if (room.current_round_id !== round.id) return;
    if (revealedRef.current === round.id) return;
    if (!(allSubmitted || remaining === 0)) return;
    revealedRef.current = round.id;
    void doReveal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSubmitted, remaining, amHost, room.status, room.current_round_id, round.id, round.status]);

  const doReveal = async () => {
    // Compute results: clusters per team + scores
    const teams = ["purple", "green"];
    const teamResults: Record<string, any> = {};
    const scoreUpdates: Record<string, number> = {};
    for (const t of teams) {
      const teamPlayers = players.filter((p) => p.team === t);
      const teamSubs = submissions.filter((s) => teamPlayers.some((p) => p.id === s.player_id));
      const cs = cluster(teamSubs, (s) => s.value);
      const align = alignmentScore(teamSubs, (s) => s.value);
      let earned = 0;
      for (const c of cs) {
        if (c.items.length >= 2) earned += c.items.length * 100;
      }
      // Bonus: everyone matched
      if (cs.length === 1 && teamSubs.length === teamPlayers.length && teamPlayers.length >= 2) {
        earned += 200;
      }
      teamResults[t] = {
        clusters: cs.map((c) => ({ key: c.key, values: c.items.map((i) => i.value) })),
        alignment: align,
        earned,
      };
      for (const p of teamPlayers) {
        scoreUpdates[p.id] = (scoreUpdates[p.id] ?? 0) + earned;
      }
    }
    const overallAlign = alignmentScore(submissions, (s) => s.value);
    await supabase
      .from("rounds")
      .update({ status: "revealed", results: { teams: teamResults, overallAlign } })
      .eq("id", round.id);
    await Promise.all(
      Object.entries(scoreUpdates).map(([pid, add]) => {
        const cur = players.find((p) => p.id === pid)?.score ?? 0;
        return supabase.from("players").update({ score: cur + add }).eq("id", pid);
      }),
    );
    await supabase.from("rooms").update({ status: "revealing" }).eq("id", room.id);
  };

  const submit = async () => {
    if (!answer.trim() || mySub) return;
    setSubmitting(true);
    const { error } = await supabase
      .from("submissions")
      .insert({ round_id: round.id, player_id: me.id, value: answer.trim() });
    if (!error) setAnswer("");
    setSubmitting(false);
  };

  const pct = total > 0 ? (remaining / total) * 100 : 0;
  const ringColor = remaining <= 5 ? "stroke-destructive" : remaining <= 10 ? "stroke-warning" : "stroke-primary";

  return (
    <main className="min-h-screen px-4 pb-6">
      <RoomHeader room={room} me={me} amHost={amHost} players={players} />
      <div className="max-w-xl mx-auto space-y-6 animate-pop-in pt-2">
        <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
          <span>ROUND {round.round_number} / {room.settings.rounds ?? 5}</span>
          <span>{submissions.length} / {players.length} submitted</span>
        </div>

        <Card className="p-6 text-center border-2 shadow-pop bg-gradient-to-br from-card to-primary/5">
          <div className="text-xs font-bold uppercase tracking-wider text-primary mb-2">Prompt</div>
          <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight">{round.prompt}</h2>
        </Card>

        <div className="flex items-center justify-center">
          <div className="relative h-28 w-28">
            <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="44" className="fill-none stroke-muted" strokeWidth="8" />
              <circle
                cx="50"
                cy="50"
                r="44"
                className={`fill-none ${ringColor} transition-all`}
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${(pct * 276.46) / 100} 276.46`}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div className="text-3xl font-extrabold tabular-nums">{remaining}</div>
            </div>
          </div>
        </div>

        {!mySub ? (
          <Card className="p-4 space-y-3 border-2 shadow-card">
            <Input
              autoFocus
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Type your answer…"
              maxLength={40}
              className="h-14 text-lg text-center font-semibold"
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
            <Button
              onClick={submit}
              disabled={!answer.trim() || submitting}
              size="lg"
              className="w-full h-12 gradient-primary font-bold"
            >
              Submit
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              Match your teammates to score points!
            </p>
          </Card>
        ) : (
          <Card className="p-6 text-center border-2 shadow-card bg-success/5">
            <div className="inline-flex h-12 w-12 rounded-full bg-success items-center justify-center mb-2">
              <Check className="h-6 w-6 text-white" strokeWidth={3} />
            </div>
            <div className="font-bold">Locked in: "{mySub.value}"</div>
            <div className="text-sm text-muted-foreground mt-1">Waiting for others…</div>
          </Card>
        )}

        <div className="flex flex-wrap justify-center gap-2">
          {players.map((p) => {
            const submitted = submissions.some((s) => s.player_id === p.id);
            const team = p.team === "purple" ? "bg-team-purple" : "bg-team-green";
            return (
              <div
                key={p.id}
                className={`flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-semibold ${
                  submitted ? "border-success bg-success/10" : "border-border"
                }`}
              >
                <span className={`h-2 w-2 rounded-full ${team}`} />
                {p.name}
                {submitted && <Check className="h-3 w-3 text-success" strokeWidth={3} />}
              </div>
            );
          })}
        </div>

        {amHost && (
          <Button onClick={doReveal} variant="outline" className="w-full" size="sm">
            End round early & reveal
          </Button>
        )}
      </div>
    </main>
  );
}
